import {
    Box,
    MenuItem,
    Checkbox as MuiCheckbox,
    TextField as MuiTextField,
    Tooltip,
} from '@mui/material';

import MaterialTable from 'material-table';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Edit from '@mui/icons-material/Edit';
import { useDisclosure } from '@chakra-ui/react';

import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import tableIcons from '../../../utils/MaterialTableIcons';
import BillingNotesModal from './BillingNotesModal';

import store from '@/store/store';
import walkthroughIds from '../walkthroughIds';
import { customTablePaperStyle } from './constants';

const Checkbox = forwardRef((props, ref) => {
    return (
        <MuiCheckbox
            sx={{
                '&.Mui-checked': {
                    color: 'var(--chakra-colors-brand-500)',
                },
            }}
            ref={ref}
            {...props}
        />
    );
});

Checkbox.displayName = 'Checkbox';

const TextField = forwardRef((props, ref) => {
    const { darkMode } = useSelector((state) => state.settings);
    return (
        <MuiTextField
            SelectProps={{
                MenuProps: {
                    PaperProps: {
                        sx: customTablePaperStyle(darkMode),
                    },
                },
            }}
            ref={ref}
            {...props}
        />
    );
});

TextField.displayName = 'TextField';

// default columns for the table
const columns = [
    {
        field: 'description',
        title: ' Billing Note',
        width: 570,
        editComponent: (props) => {
            return (
                <TextField
                    variant="standard"
                    size="small"
                    value={props.value}
                    onChange={(e) => {
                        props.onChange(e.target.value);
                    }}
                />
            );
        },
    },
    {
        field: 'requiredScope',
        title: 'Scope',
        width: 125,
        lookup: {
            20: 'Company',
            40: 'Partner',
            60: 'Wholesaler',
            80: 'Global',
        },
        editComponent: (props) => {
            return (
                <TextField
                    select
                    variant="standard"
                    size="small"
                    value={props.value}
                    onChange={(e) => props.onChange(e.target.value)}>
                    {scopes.map((option) => (
                        <MenuItem key={option.label} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            );
        },
    },
    {
        field: 'isActive',
        title: 'Active',
        width: 120,
        type: 'boolean',
        render: (rowData) => {
            return (
                <Tooltip title="Enable edit mode to change">
                    <Checkbox
                        onMouseDown={() => {
                            store.dispatch({
                                type: 'CHANGE_MODAL_STATE',
                                payload: {
                                    rowClicked: rowData?.tableData?.id,
                                },
                            });
                        }}
                        onMouseLeave={() => {
                            store.dispatch({
                                type: 'CHANGE_MODAL_STATE',
                                payload: {
                                    rowClicked: -1,
                                },
                            });
                        }}
                        checked={rowData.isActive}
                        className="hover:cursor-not-allowed"
                        readOnly
                    />
                </Tooltip>
            );
        },
        editComponent: (props) => {
            return (
                <Checkbox
                    checked={props?.rowData.isActive}
                    onChange={(e) => props.onChange(e.target.checked)}
                />
            );
        },
    },
    {
        field: 'billingNoteLocation',
        title: 'Display Location',
        width: 220,
        render: (rowData) => {
            if (rowData.billingNoteLocation === 0) {
                return 'Top';
            }
            if (rowData.billingNoteLocation === 1) {
                return 'Bottom';
            } else {
                return 'Top';
            }
        },
        editComponent: (props) => {
            return (
                <TextField
                    select
                    variant="standard"
                    size="small"
                    value={props.value}
                    onChange={(e) => props.onChange(e.target.value)}>
                    {locations.map((option) => (
                        <MenuItem key={option.label} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            );
        },
    },
];
// enums for inputs
const scopes = [
    { value: 20, label: 'Company' },
    { value: 40, label: 'Partner' },
    { value: 60, label: 'Wholesaler' },
    { value: 80, label: 'Global' },
];
const locations = [
    { value: 0, label: 'Top' },
    { value: 1, label: 'Bottom' },
];

/**
 * Table that is displayed in the billing note section of the company billing form
 */
const BillingNoteTable = (props) => {
    // Helper function to get billing settings based on mode
    const getBillingSettings = (state, mode) => {
        switch (mode) {
            case 'Wholesaler':
                return state.wholesalerBillingSettings;
            case 'Partner':
                return state.partnerBillingSettings;
            case 'Company':
            default:
                return state.companyBillingSettings;
        }
    };

    const muiTheme = useMemo(
        () =>
            createTheme({
                typography: {
                    color: '#000000',
                },
                head: {
                    backgroundColor: null,
                },
                overrides: {
                    MuiToolbar: {
                        root: {
                            color: 'rgba(255,255,255, 0.7)',
                            backgroundColor: '#424242',
                        },
                    },
                    MuiTable: {
                        root: {
                            WebkitTextFillColor:
                                'rgba(255, 255, 255, 0.7) !important',
                            backgroundColor: '#424242 !important',
                        },
                    },
                    MuiTableHead: {
                        root: {
                            backgroundColor: '#424242 !important',
                        },
                    },
                    MuiTableCell: {
                        root: {
                            borderBottom: null,
                            borderTop: '1px solid rgba(224, 224, 224, 0.1)',
                        },
                    },
                    MuiTablePagination: {
                        root: {
                            backgroundColor: 'white',
                        },
                    },
                    MuiPaper: {
                        root: {
                            backgroundColor: null,
                        },
                    },
                    MuiIconButton: {
                        label: {
                            color: 'rgba(255, 255, 255, 0.3)',
                        },
                    },
                },
            }),
        [],
    );

    const { setBillingState, deletedBillingNoteIDs, setDeletedBillingNoteIDs } =
        props;
    const [isActive, setIsActive] = useState(true);
    const [desc, setDesc] = useState();
    const [location, setLocation] = useState(0);
    const [scope, setScope] = useState(20);
    const [rows, setRows] = useState([]);
    const [loading] = useState(false);

    // state access
    const modalState = useSelector((state) => ({ ...state.modal }));
    const state = props.formData ? props.formData : modalState;

    const { darkMode } = useSelector((state) => state.settings);

    const { isOpen, onOpen, onClose } = useDisclosure();

    const { modal } = walkthroughIds.companies.billing;

    // function for when the modal is submitted, ie a note is added
    const onSubmitHandler = () => {
        const billingSettings = getBillingSettings(state, props.mode);
        let tempNotes;
        if (billingSettings && billingSettings.billingNotes) {
            tempNotes = [
                ...billingSettings.billingNotes,
                {
                    requiredScope: scope,
                    billingNoteLocation: location,
                    description: desc,
                    isActive: isActive,
                    billingSettingsID: billingSettings.id,
                },
            ];
        } else {
            tempNotes = [
                {
                    requiredScope: scope,
                    billingNoteLocation: location,
                    description: desc,
                    isActive: isActive,
                    billingSettingsID: billingSettings?.id,
                },
            ];
        }
        setBillingState({ billingNotes: tempNotes });
        onClose();
    };

    // append rows based on the state
    useEffect(() => {
        const billingSettings = getBillingSettings(state, props.mode);
        if (billingSettings?.billingNotes && loading === false) {
            let result = [];
            billingSettings.billingNotes.forEach((v) => {
                const temp = {
                    ...v,
                };
                result.push(temp);
            });
            setRows([...result]);
        }
    }, [state?.companyBillingSettings?.billingNotes, state?.partnerBillingSettings?.billingNotes, state?.wholesalerBillingSettings?.billingNotes, loading, props.mode]);

    const billingNotesModalProps = {
        isOpen,
        onClose,
        modal,
        desc,
        setDesc,
        scopes,
        setScope,
        locations,
        location,
        setLocation,
        isActive,
        setIsActive,
        onSubmitHandler,
    };

    // table that billing notes renders
    return (
        <ThemeProvider theme={darkMode ? muiTheme : null}>
            <Box
                className="billing-note-table"
                sx={{
                    th: {
                        backgroundColor: darkMode
                            ? '#6a696a !important'
                            : '#FFFFFF !important',
                    },
                    thead: {
                        th: {
                            backgroundColor: darkMode
                                ? 'var(--chakra-colors-gray-600)!important'
                                : 'var(--chakra-colors-gray-200)!important',
                        },
                        span: {
                            fontSize: '16px',
                            fontWeight: '600',
                        },
                    },
                    td: {
                        color: darkMode ? 'black !important' : null,
                    },
                    boxShadow: darkMode ? 1 : 0,
                }}>
                <MaterialTable
                    title="Billing Notes"
                    icons={{
                        ...tableIcons,
                        Edit,
                    }}
                    columns={columns}
                    data={rows}
                    options={{
                        actionsColumnIndex: -1,
                        search: false,
                        sorting: true,
                        pageSize: 3,
                        pageSizeOptions: [],
                        draggable: false,
                    }}
                    actions={[
                        {
                            icon: tableIcons.Add,
                            tooltip: 'Add',
                            isFreeAction: true,
                            onClick: onOpen,
                        },
                    ]}
                    editable={{
                        onRowDelete: (oldData) =>
                            new Promise((resolve) => {
                                setTimeout(() => {
                                    const dataDelete = [...rows];
                                    console.log(oldData);
                                    const index = oldData.tableData.id;
                                    dataDelete.splice(index, 1);
                                    setRows([...dataDelete]);
                                    setBillingState({
                                        billingNotes: dataDelete,
                                    });

                                    // Track deleted note ID if it exists (for DELETE API call)
                                    if (
                                        oldData.id &&
                                        setDeletedBillingNoteIDs &&
                                        deletedBillingNoteIDs
                                    ) {
                                        setDeletedBillingNoteIDs([
                                            ...deletedBillingNoteIDs,
                                            oldData.id,
                                        ]);
                                    }

                                    resolve();
                                }, 0);
                            }),
                        onRowUpdate: (newData, oldData) =>
                            new Promise((resolve) => {
                                setTimeout(() => {
                                    const dataUpdate = [...rows];
                                    const index = oldData.tableData.id;
                                    dataUpdate[index] = newData;
                                    setRows([...dataUpdate]);

                                    // Update the billing notes in state
                                    const billingSettings = getBillingSettings(state, props.mode);
                                    const result =
                                        billingSettings?.billingNotes?.map(
                                            (v) => {
                                                // Match by id if it exists
                                                if (
                                                    oldData.id &&
                                                    v.id === oldData.id
                                                ) {
                                                    // Update the note with matching ID
                                                    return {
                                                        ...newData,
                                                        id: v.id,
                                                    };
                                                } else if (
                                                    !oldData.id &&
                                                    v.description ===
                                                        oldData.description &&
                                                    v.requiredScope ===
                                                        oldData.requiredScope &&
                                                    v.isActive ===
                                                        oldData.isActive &&
                                                    v.billingNoteLocation ===
                                                        oldData.billingNoteLocation
                                                ) {
                                                    // For new notes without ID, match by all fields
                                                    return {
                                                        ...newData,
                                                    };
                                                }
                                                // Keep all other notes unchanged
                                                return v;
                                            },
                                        );

                                    setBillingState({
                                        billingNotes: result,
                                    });
                                    resolve();
                                }, 1000);
                            }),
                    }}
                    localization={{
                        header: {
                            actions: '',
                        },
                        body: {
                            editRow: {
                                deleteText:
                                    'Are you sure you want to delete this note?',
                            },
                        },
                    }}
                />
            </Box>

            {/* Diaglogue that opens when you add a billing note */}
            <BillingNotesModal {...billingNotesModalProps} />
        </ThemeProvider>
    );
};

export default BillingNoteTable;
